/**
 * Which site this bundle is currently serving.
 *
 * Toukky ships one build to two hostnames. On the customer site the admin
 * routes are not mounted at all; on the admin host the customer routes are
 * not. Same JavaScript, two different applications, decided at runtime from
 * the hostname.
 *
 * ## This is not a security boundary
 *
 * The bundle is identical on both hosts, so the admin code is downloadable
 * either way and anyone can call the API directly from a terminal. Nothing
 * here authorises anything. The protection is entirely server-side: every
 * admin endpoint re-checks the caller's role against the database
 * (backend/admin_auth.py), and that is what actually stops a customer reading
 * a booking that is not theirs.
 *
 * What splitting the hostname does buy is real, just narrower: an operations
 * tool stops being one URL guess away on the site the public uses, staff stop
 * being one mistyped path from it, and the admin host can later get controls
 * the public site cannot have — an IP allowlist, its own certificate, a
 * different session lifetime.
 */

const DEFAULT_ADMIN_HOSTS = ["admin.toukky.com"];

/** Hosts that serve the dashboard. Configurable so staging and local work. */
export function adminHosts() {
  const configured = (process.env.REACT_APP_ADMIN_HOST || "")
    .split(",")
    .map((h) => h.trim().toLowerCase())
    .filter(Boolean);
  return configured.length ? configured : DEFAULT_ADMIN_HOSTS;
}

/** The dashboard's front door: its own hostname in production, the local
 * "admin." alias when developing. */
export function adminEntry(hostname) {
  const host = (hostname ?? (typeof window !== "undefined" ? window.location.hostname : "")).toLowerCase().split(":")[0];
  if (host === "localhost" || host === "127.0.0.1") {
    const port = typeof window !== "undefined" && window.location.port ? `:${window.location.port}` : "";
    return `${window.location.protocol}//admin.localhost${port}/`;
  }
  return `https://${adminHosts()[0]}/`;
}

export function isAdminHost(hostname) {
  const host = (hostname ?? (typeof window !== "undefined" ? window.location.hostname : ""))
    .toLowerCase()
    // A port never belongs in a hostname comparison, but callers pass
    // window.location.host by mistake often enough to be worth handling.
    .split(":")[0];
  if (!host) return false;
  // "admin.localhost" and "admin.127.0.0.1" let the split be exercised locally
  // without editing hosts files or juggling two dev servers.
  if (host.startsWith("admin.")) return true;
  return adminHosts().includes(host);
}

/**
 * A dashboard path, with the old /admin prefix taken off if it is there.
 *
 * The dashboard used to live at /admin on the customer site and now sits at
 * the root of its own hostname, so a link to "/admin/bookings" matches no
 * route, falls through to the catch-all and lands the operator back on the
 * overview they just clicked away from. The server still writes some of these
 * (the needs-attention queue), and so did a couple of older pages.
 *
 * Normalising in the browser rather than only at the source is deliberate:
 * the two deploy separately, so a backend that has not shipped yet must not
 * be able to produce a dead link, and neither must a row written before the
 * move. "/administration" is left alone — only a whole path segment counts.
 */
export function adminPath(href) {
  if (typeof href !== "string" || !href.startsWith("/admin")) return href;
  const rest = href.slice("/admin".length);
  if (rest === "") return "/";
  if (!"/?#".includes(rest[0])) return href;
  return rest.startsWith("/") ? rest : "/" + rest;
}
